/* eslint-disable max-lines, max-lines-per-function */
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Loader, Pin } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  artboardBackgroundAtom,
  mobileSizePlacementOpenAtom,
  useArtworkForm,
  useEnhancedImageSource,
  useImagesWarmFastCache
} from "@design-lab/entities/artwork";
import {
  activePinIdAtom,
  currentPinningSessionAtom,
  escapeHatchTriggerAtom,
  nonGenerativeReadyCounterAtom,
  pendingInitialMessageAtom,
  pinBasedRedrawOverrideAtom,
  pinningImageApprovalAtom,
  pinningImageLoadedAtom,
  pinningImageSavingAtom,
  pinningModeAtom,
  pinningPanelExpandedAtom,
  pinningSessionsAtom,
  pinsAtom,
  showPinsAtom,
  SINGLE_PIN_LIMIT,
  useBulkDeletePins,
  useCancelPinningSession,
  useCompletePinningSession,
  useFetchPins,
  useRetryPreview
} from "@design-lab/entities/pinning";
import { ConfirmActionModal } from "@design-lab/features/design-artwork/ui/ConfirmActionModal";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import {
  trackDesignLabPinningSessionCompleted,
  trackDesignLabPinningSessionCompletionFailed,
  trackPinSessionEscapeHatchFailed,
  trackPinsSessionCancellationAborted,
  trackPinsSessionCancelled,
  trackPinsSessionEditCancelFailed,
  trackPinsSessionEditCancelModalConfirmed,
  trackPinsSessionEditCancelModalStayed,
  trackPinSessionsGenPreviewBeforeClicked,
  trackPinSessionsGenPreviewAfterClicked,
  trackPinSessionGeneratedImageShown
} from "@design-lab/shared/lib/analytics";

import { useToast } from "@design-lab/shared/ui";
import { Switch } from "@design-lab/shared/ui/design-system/Switch";
import { useArtboard } from "@design-lab/widgets/artboard/model/useArtboard";
import { ArtboardBase } from "../ArtboardBase";
import { RefineZoom } from "../refine-artwork/RefineZoom";
import { BeforeAfterToggle } from "./BeforeAfterToggle";
import { PinningActionBar } from "./PinningActionBar";
import { PinningFeedback } from "./PinningFeedback";
import { PinningHeader } from "./PinningHeader";
import { PinningPanel } from "./PinningPanel";
import { useEscapeHatch } from "./useEscapeHatch";

type PinningTooltipProps = {
  message: string;
};

function PinningTooltip({ message }: PinningTooltipProps) {
  return (
    <div
      data-testid="pinning-artboard-tooltip"
      className="absolute left-2 top-2 z-10 flex max-w-[400px] items-center gap-2 rounded-lg bg-white px-3 py-2 shadow-md"
    >
      <Pin className="h-5 w-5 shrink-0 -rotate-45 text-blue-500" />
      <span className="whitespace-pre-line text-sm font-medium text-blue-700">{message}</span>
    </div>
  );
}

// eslint-disable-next-line max-lines-per-function
export function PinningArtboard() {
  const { isDesktop } = useResponsive();
  const { composition, placement } = useArtboard();
  useArtworkForm();
  const side = placement === "back" ? "back" : "front";
  const { source } = useEnhancedImageSource({ side });

  const [backgroundType, setBackgroundType] = useAtom(artboardBackgroundAtom);
  const [isMobileSizePlacementOpen, setIsMobileSizePlacementOpen] = useAtom(
    mobileSizePlacementOpenAtom
  );
  const artboardRef = useRef<HTMLDivElement>(null);

  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const [currentPinningSession, setCurrentPinningSession] = useAtom(currentPinningSessionAtom);
  const [pins, setPins] = useAtom(pinsAtom);
  const [activePinId, setActivePinId] = useAtom(activePinIdAtom);
  const [showPins, setShowPins] = useAtom(showPinsAtom);
  const setPinningMode = useSetAtom(pinningModeAtom);
  const isPanelExpanded = useAtomValue(pinningPanelExpandedAtom);
  const [pendingInitialMessage, setPendingInitialMessage] = useAtom(pendingInitialMessageAtom);
  const [tooltipMode, setTooltipMode] = useState<"initial" | "add-next" | null>("initial");
  const [isRetryingPreview, setIsRetryingPreview] = useState(false);
  const retryingPreviewRef = useRef(false);
  const [cancelModalType, setCancelModalType] = useState<"editSession" | "newSession" | null>(null);
  const setPinBasedRedrawOverride = useSetAtom(pinBasedRedrawOverrideAtom);
  const escapeHatchTrigger = useAtomValue(escapeHatchTriggerAtom);
  const isImageSaving = useAtomValue(pinningImageSavingAtom);
  const [nonGenerativeReadyCounter, setNonGenerativeReadyCounter] = useAtom(
    nonGenerativeReadyCounterAtom
  );
  const { mutate: cancelSession, isPending: isCancelling } = useCancelPinningSession();
  const { error: showErrorToast } = useToast();
  const { mutate: bulkDeletePins, isPending: isBulkDeleting } = useBulkDeletePins();
  const { mutate: completeSession } = useCompletePinningSession();
  const { mutateAsync: retryPreview } = useRetryPreview();
  const { warmCache } = useImagesWarmFastCache();
  const [imageApproval, setImageApproval] = useAtom(pinningImageApprovalAtom);
  const setPinningImageLoaded = useSetAtom(pinningImageLoadedAtom);
  const [editedImage, setEditedImage] = useState<HTMLImageElement | null>(null);
  const [beforeAfterView, setBeforeAfterView] = useState<"before" | "after">("after");

  // Pre-warm mask cache when pins change
  useEffect(() => {
    const maskUrls = pins
      .map(pin => pin.segmentationMaskUrl)
      .filter((url): url is string => url != null);
    if (maskUrls.length > 0) {
      warmCache(maskUrls);
    }
  }, [pins, warmCache]);

  // Load edited image when approval is set
  useEffect(() => {
    let cancelled = false;
    if (!imageApproval) {
      setEditedImage(null);
      setPinningImageLoaded(false);
      return undefined;
    }
    setPinningImageLoaded(false);
    setBeforeAfterView("after");
    warmCache([imageApproval.imageUrl]).then(([result]) => {
      if (result && !cancelled) {
        setEditedImage(result.image);
        setPinningImageLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [imageApproval, warmCache, setPinningImageLoaded]);

  // Get current session info
  const currentArtworkId = currentPinningSession?.artworkId;
  const currentDesignProjectPlacementId = currentPinningSession?.designProjectPlacementId;
  const currentSessionId = currentPinningSession?.sessionId;

  useEffect(() => {
    if (!editedImage || !imageApproval || !currentSessionId || !currentArtworkId) return;
    trackPinSessionGeneratedImageShown({
      sessionId: currentSessionId,
      artworkId: currentArtworkId,
      generatedImageUrl: imageApproval.imageUrl,
      pinId: imageApproval.pinId
    });
  }, [editedImage, imageApproval, currentSessionId, currentArtworkId]);

  const handleBeforeAfterChange = useCallback(
    (view: "before" | "after") => {
      setBeforeAfterView(view);
      if (!currentSessionId || !currentArtworkId || !imageApproval) return;
      const payload = {
        sessionId: currentSessionId,
        artworkId: currentArtworkId,
        generatedImageUrl: imageApproval.imageUrl
      };
      if (view === "before") {
        trackPinSessionsGenPreviewBeforeClicked(payload);
      } else {
        trackPinSessionsGenPreviewAfterClicked(payload);
      }
    },
    [currentSessionId, currentArtworkId, imageApproval]
  );

  // Check if there's at least one completed pin
  const hasCompletedPin = pins.some(pin => pin.state === "ready");
  const readyPinsCount = pins.filter(pin => pin.state === "ready").length;

  // Compute new pins (pins created during this editing session)
  const newPins = useMemo(() => pins.filter(p => p.isNew), [pins]);
  const newPinIdsArray = useMemo(() => newPins.map(p => p.id), [newPins]);
  const hasNewPins = newPins.length > 0;

  // Determine if we're editing an existing session (has any pins that aren't new)
  const isEditingExistingSession = useMemo(() => pins.some(p => !p.isNew), [pins]);

  // Derive active pin and whether user can add new pins
  const activePin = pins.find(p => p.id === activePinId);
  const canAddNewPin =
    pins.length < SINGLE_PIN_LIMIT &&
    (!activePin || activePin.state === "ready" || activePin.state === "unsupported");

  const hasUnfinishedPin = pins.some(
    pin =>
      pin.state === "draft" || pin.state === "processing" || pin.state === "needs_clarification"
  );

  // Manage tooltip visibility based on pin states
  useEffect(() => {
    if (pendingInitialMessage) {
      if (tooltipMode !== null) setTooltipMode(null);
      return;
    }

    if (hasUnfinishedPin) {
      if (tooltipMode !== null) setTooltipMode(null);
      return;
    }

    if (pins.length === 0) {
      if (tooltipMode !== "initial") setTooltipMode("initial");
      return;
    }

    if (hasCompletedPin && canAddNewPin) {
      if (tooltipMode !== "add-next") setTooltipMode("add-next");
      return;
    }

    if (tooltipMode !== null) setTooltipMode(null);
  }, [pins, hasCompletedPin, canAddNewPin, tooltipMode, hasUnfinishedPin, pendingInitialMessage]);

  // Hydration: fetch existing pins on mount
  const { data: sessionData, isLoading: isLoadingPins } = useFetchPins(currentSessionId ?? null);

  useEffect(() => {
    if (!sessionData) return;
    if (imageApproval) return;

    const pinsData = sessionData?.pins ?? [];

    if (Array.isArray(pinsData) && pinsData.length > 0) {
      setPins(
        pinsData.map((p: any) => {
          const messages = Array.isArray(p.messages)
            ? p.messages
                .filter((m: any) => m?.role !== "ai_ack")
                .map((m: any) => ({
                  id: m.id || "",
                  role: m.role || "user",
                  content: m.content || "",
                  timestamp: m.timestamp || Date.now(),
                  ...(m.options ? { options: m.options } : {}),
                  ...(m.questionType ? { questionType: m.questionType } : {}),
                  ...(m.clarifications ? { clarifications: m.clarifications } : {}),
                  ...(m.textInfo ? { textInfo: m.textInfo } : {})
                }))
            : [];

          // If no messages but we have message field, create a synthetic AI message
          if (messages.length === 0 && p.message) {
            messages.push({
              id: `msg-${p.pinId}`,
              role: "ai",
              content: p.message,
              timestamp: Date.now()
            });
          }

          // Map API status to internal state
          const state = p.status || p.state || "draft";

          // Pins from API are existing (not new)
          return {
            id: p.pinId || p.id || "",
            coordinates: p.coordinates || { x: 0, y: 0 },
            state,
            instruction: p.instruction || p.summary,
            messages,
            areasOfInterest: p.areaOfInterest,
            segmentationMaskUrl: p.segmentationMaskUrl,
            isNew: false
          };
        })
      );
    }

    const generatedImageUrl = sessionData?.generatedImageUrl;
    if (generatedImageUrl && currentSessionId && currentDesignProjectPlacementId) {
      const readyPin = pinsData.find((p: any) => (p.status || p.state) === "ready");
      const pinId = (readyPin as any)?.pinId || readyPin?.pin_id || "";
      setImageApproval({
        pinId,
        imageUrl: generatedImageUrl,
        sessionId: currentSessionId,
        designProjectPlacementId: currentDesignProjectPlacementId,
        summary: (readyPin as any)?.instruction || null
      });
      setActivePinId(pinId);
      return;
    }

    const readyPin = pinsData.find((p: any) => (p.status || p.state) === "ready");
    const sessionState =
      currentDesignProjectPlacementId && pinningSessions[currentDesignProjectPlacementId]?.state;
    const needsPreviewRetry =
      readyPin &&
      currentSessionId &&
      currentDesignProjectPlacementId &&
      (sessionState === "active" || sessionState === "generating_preview");

    if (needsPreviewRetry && !retryingPreviewRef.current) {
      const pinId = (readyPin as any).pinId || readyPin.pin_id || "";
      setActivePinId(pinId);
      retryingPreviewRef.current = true;
      setIsRetryingPreview(true);
      retryPreview({ sessionId: currentSessionId })
        .then(result => {
          if (result.previewReady && result.generatedImageUrl) {
            setImageApproval({
              pinId,
              imageUrl: result.generatedImageUrl,
              sessionId: currentSessionId,
              designProjectPlacementId: currentDesignProjectPlacementId!,
              summary: (readyPin as any).instruction || null
            });
            setActivePinId(pinId);
            setPinningSessions(prev => ({
              ...prev,
              [currentDesignProjectPlacementId!]: {
                sessionId: currentSessionId,
                state: result.state,
                summary: prev[currentDesignProjectPlacementId!]?.summary ?? null
              }
            }));
          } else {
            completeSession(currentSessionId, {
              onSuccess: data => {
                setPinningSessions(prev => ({
                  ...prev,
                  [currentDesignProjectPlacementId!]: {
                    sessionId: currentSessionId,
                    state: "completed",
                    summary: data.summary ?? null
                  }
                }));
                setCurrentPinningSession(null);
                setPinningMode(false);
              }
            });
          }
        })
        .catch(() => {
          completeSession(currentSessionId, {
            onSuccess: data => {
              setPinningSessions(prev => ({
                ...prev,
                [currentDesignProjectPlacementId!]: {
                  sessionId: currentSessionId,
                  state: "completed",
                  summary: data.summary ?? null
                }
              }));
              setCurrentPinningSession(null);
              setPinningMode(false);
            }
          });
        })
        .finally(() => {
          retryingPreviewRef.current = false;
          setIsRetryingPreview(false);
        });
    }
  }, [
    sessionData,
    setPins,
    imageApproval,
    setImageApproval,
    setActivePinId,
    currentSessionId,
    currentDesignProjectPlacementId,
    pinningSessions,
    retryPreview,
    completeSession,
    setPinningSessions,
    setCurrentPinningSession,
    setPinningMode
  ]);

  // Exit pinning mode without cancelling the session (for editing existing sessions)
  const exitPinningMode = useCallback(() => {
    setPins([]);
    setCurrentPinningSession(null);
    setPendingInitialMessage(null);
    setImageApproval(null);
    setPinningMode(false);
  }, [
    setPins,
    setCurrentPinningSession,
    setPendingInitialMessage,
    setImageApproval,
    setPinningMode
  ]);

  const handleEscapeHatchConfirmAction = useCallback(() => {
    if (!currentDesignProjectPlacementId || !currentSessionId) return;

    const cleanupPinningState = () => {
      setPinningSessions(prev => {
        const next = { ...prev };
        delete next[currentDesignProjectPlacementId];
        return next;
      });
      setPinBasedRedrawOverride(false);
      exitPinningMode();
    };

    cancelSession(
      { sessionId: currentSessionId },
      {
        onSuccess: () => {
          cleanupPinningState();
        },
        onError: (error: Error) => {
          trackPinSessionEscapeHatchFailed({
            triggerType: escapeHatchTrigger ?? "error",
            pinCount: pins.length,
            messageCount: activePin?.messages.length ?? 0,
            errorMessage: error?.message || "Unknown error"
          });
          showErrorToast({
            title: "Something went wrong",
            description:
              "We couldn't cancel the pin session, but you can still use the classic edit form."
          });
          cleanupPinningState();
        }
      }
    );
  }, [
    currentDesignProjectPlacementId,
    currentSessionId,
    cancelSession,
    setPinningSessions,
    setPinBasedRedrawOverride,
    exitPinningMode,
    pins,
    activePin,
    escapeHatchTrigger,
    showErrorToast
  ]);

  const {
    showEscapeHatch,
    escapeHatchModalOpen,
    handleEscapeHatchClick,
    handleEscapeHatchConfirm,
    handleEscapeHatchDismiss
  } = useEscapeHatch({ onConfirm: handleEscapeHatchConfirmAction });

  const escapeHatchButton = showEscapeHatch ? (
    <button
      data-testid="escape-hatch-button"
      className="cursor-pointer text-xs font-normal text-gray-400 hover:text-gray-500 hover:underline"
      onClick={handleEscapeHatchClick}
      type="button"
    >
      Use classic edit form
    </button>
  ) : null;

  // Cancel and delete the entire session
  const cancelAndDeleteSession = useCallback(() => {
    if (!currentArtworkId || !currentDesignProjectPlacementId || !currentSessionId) return;

    trackPinsSessionCancelled({
      artworkId: currentArtworkId,
      sessionId: currentSessionId,
      pinsCount: pins.length,
      readyPinsCount
    });
    cancelSession(
      { sessionId: currentSessionId, artworkId: currentArtworkId },
      {
        onSuccess: () => {
          setPinningSessions(prev => {
            const next = { ...prev };
            delete next[currentDesignProjectPlacementId];
            return next;
          });
          exitPinningMode();
        }
      }
    );
  }, [
    currentArtworkId,
    currentDesignProjectPlacementId,
    currentSessionId,
    pins.length,
    readyPinsCount,
    exitPinningMode,
    cancelSession,
    setPinningSessions
  ]);

  // Handle cancel button click
  const handleCancelClick = useCallback(() => {
    if (!currentArtworkId || !currentSessionId) {
      exitPinningMode();
      return;
    }

    // If editing an existing session with new pins, show confirmation modal
    if (isEditingExistingSession && hasNewPins) {
      setCancelModalType("editSession");
      return;
    }

    // If editing an existing session with no new pins, just exit without API call
    if (isEditingExistingSession && !hasNewPins) {
      trackPinsSessionCancelled({
        artworkId: currentArtworkId,
        sessionId: currentSessionId,
        pinsCount: pins.length,
        readyPinsCount
      });
      exitPinningMode();
      return;
    }

    // New session: show confirmation warning
    setCancelModalType("newSession");
  }, [
    currentArtworkId,
    currentSessionId,
    isEditingExistingSession,
    hasNewPins,
    pins.length,
    readyPinsCount,
    exitPinningMode
  ]);

  // Handle confirmation modal confirm (remove new pins or cancel session)
  const handleConfirmCancel = useCallback(() => {
    if (!currentSessionId) return;

    // For editing existing session: bulk delete only new pins
    if (cancelModalType === "editSession") {
      trackPinsSessionEditCancelModalConfirmed({
        sessionId: currentSessionId,
        newPinsCount: newPinIdsArray.length
      });

      // Separate synced vs unsynced pins
      // Unsynced pins are in draft state with no messages - they only exist locally
      const unsyncedPinIds = newPins
        .filter(p => p.state === "draft" && p.messages.length === 0)
        .map(p => p.id);
      const syncedPinIds = newPinIdsArray.filter(id => !unsyncedPinIds.includes(id));

      // Always remove unsynced pins locally
      if (unsyncedPinIds.length > 0) {
        setPins(prev => prev.filter(p => !unsyncedPinIds.includes(p.id)));
      }

      // If all pins were unsynced, just exit without API call
      if (syncedPinIds.length === 0) {
        setCancelModalType(null);
        exitPinningMode();
        return;
      }

      // Only call API for synced pins
      bulkDeletePins(
        { sessionId: currentSessionId, pinIds: syncedPinIds },
        {
          onSuccess: () => {
            setCancelModalType(null);
            exitPinningMode();
          },
          onError: (error: Error) => {
            trackPinsSessionEditCancelFailed({
              sessionId: currentSessionId,
              newPinsCount: syncedPinIds.length,
              errorMessage: error?.message || "Unknown error"
            });
          }
        }
      );
      return;
    }

    // For new session: cancel entire session
    setCancelModalType(null);
    cancelAndDeleteSession();
  }, [
    currentSessionId,
    cancelModalType,
    newPinIdsArray,
    newPins,
    setPins,
    bulkDeletePins,
    exitPinningMode,
    cancelAndDeleteSession
  ]);

  // Handle confirmation modal stay (close modal, stay on page)
  const handleStayOnPage = useCallback(() => {
    if (currentSessionId && currentArtworkId) {
      if (cancelModalType === "editSession") {
        trackPinsSessionEditCancelModalStayed({
          sessionId: currentSessionId,
          newPinsCount: newPinIdsArray.length
        });
      } else if (cancelModalType === "newSession") {
        trackPinsSessionCancellationAborted({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinsCount: pins.length
        });
      }
    }
    setCancelModalType(null);
  }, [currentSessionId, currentArtworkId, cancelModalType, newPinIdsArray.length, pins.length]);

  const surface = composition?.surfaces[placement];
  const frontSurface = composition?.surfaces?.front || composition?.surfaces?.frontLeftChest;
  const referenceBoundingBox =
    composition?.surfaces?.front?.boundingBox || composition?.surfaces?.frontLeftChest?.boundingBox;

  // Get all available surfaces for the current side
  const allSurfaces = [
    ...(composition?.surfaces?.front
      ? [{ surface: composition.surfaces.front, placement: "front" as const }]
      : []),
    ...(composition?.surfaces?.frontLeftChest
      ? [{ surface: composition.surfaces.frontLeftChest, placement: "frontLeftChest" as const }]
      : []),
    ...(composition?.surfaces?.back
      ? [{ surface: composition.surfaces.back, placement: "back" as const }]
      : [])
  ].filter(s => (placement === "back" ? s.placement === "back" : s.placement !== "back"));

  const canvasProps = {
    surface: surface || null,
    referenceBoundingBox,
    placement,
    artworkImage:
      imageApproval && beforeAfterView === "after" && editedImage
        ? editedImage
        : source.enhancedImage?.image,
    frontSurface,
    allSurfaces,
    disablePanning: false
  };

  const controls = {
    showSideSwitcher: false,
    showZoom: true,
    showBackgroundToggle: true,
    showPickAModel: true,
    showToolbar: false,
    showProductBrowser: false,
    showRemoveBackground: false,
    showSizePlacement: false
  };

  const isNewSessionCancel = cancelModalType === "newSession";
  const cancelModalProps = {
    isOpen: cancelModalType !== null,
    onClose: handleStayOnPage,
    onConfirm: handleConfirmCancel,
    title: isNewSessionCancel ? "Leave without saving?" : "Remove new pins?",
    message: isNewSessionCancel
      ? "Your edit request will be lost."
      : `This will remove ${newPinIdsArray.length} pin${newPinIdsArray.length === 1 ? "" : "s"}, are you sure?`,
    confirmText: isNewSessionCancel
      ? "Yes, exit"
      : `Remove pin${newPinIdsArray.length === 1 ? "" : "s"}?`,
    cancelText: isNewSessionCancel ? "Keep editing" : "Stay on page",
    isLoading: isCancelling || isBulkDeleting,
    loadingText: isNewSessionCancel ? "Exiting..." : "Removing pins...",
    destructive: isNewSessionCancel
  };

  // Auto-complete session when a pin reaches "ready" without generating an image.
  // Skipped when imageApproval is set — that means a preview_ready event was received
  // and the approve/reject flow handles session completion instead.
  useEffect(() => {
    if (nonGenerativeReadyCounter === 0) return;
    setNonGenerativeReadyCounter(0);

    if (imageApproval) return;

    if (!currentSessionId || !currentArtworkId || !currentDesignProjectPlacementId) {
      setPinningMode(false);
      return;
    }

    completeSession(currentSessionId, {
      onSuccess: data => {
        trackDesignLabPinningSessionCompleted({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinsCount: pins.length,
          readyPinsCount,
          summary: data.summary || ""
        });
        setPinningSessions(prev => ({
          ...prev,
          [currentDesignProjectPlacementId]: {
            sessionId: currentSessionId,
            state: "completed",
            summary: data.summary ?? null
          }
        }));
        setCurrentPinningSession(null);
        setPinningMode(false);
      },
      onError: (error: Error) => {
        trackDesignLabPinningSessionCompletionFailed({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          errorMessage: error?.message || "Unknown error"
        });
        showErrorToast({
          title: "Something went wrong",
          description: "Please try again."
        });
      }
    });
  }, [nonGenerativeReadyCounter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile: full-screen layout
  if (!isDesktop) {
    return (
      <div
        className="bg-background fixed inset-0 z-50 flex flex-col"
        data-testid="pinning-artboard"
      >
        {/* Header */}
        <PinningHeader />

        {/* Main content area - uses flex-col-reverse so panel expands upward */}
        <div className="flex min-h-0 flex-1 flex-col-reverse">
          {/* Pinning panel - at bottom, expands upward */}
          <div
            className={`mx-2 mb-1 overflow-hidden rounded-xl bg-white shadow-lg transition-[max-height,flex] duration-300 ease-out ${
              isPanelExpanded ? "mt-1 max-h-[100vh] min-h-0 flex-1" : "max-h-[40vh] shrink-0"
            }`}
          >
            <PinningPanel />
          </div>

          {/* Artboard area - above panel (due to reverse), shrinks when panel expands */}
          <div
            ref={artboardRef}
            className={`relative overflow-hidden transition-[max-height] duration-300 ease-out ${
              isPanelExpanded ? "max-h-0" : "max-h-[100vh] flex-1"
            }`}
            data-testid="pinning-artboard-container"
          >
            <ArtboardBase
              readonly={true}
              canvasProps={canvasProps}
              controls={{ ...controls, showZoom: false, showBackgroundToggle: false }}
              backgroundType={backgroundType}
              onBackgroundChange={setBackgroundType}
              currentSide={side}
              zoomConfig={{ initialZoom: 2, maxZoom: 10 }}
              loadingState={{ isLoading: false, imagePreview: undefined }}
              frontLoadingState={{ isLoading: false, imagePreview: undefined }}
              backLoadingState={{ isLoading: false, imagePreview: undefined }}
              mobileSizePlacementOpen={isMobileSizePlacementOpen}
              onMobileSizePlacementChange={setIsMobileSizePlacementOpen}
            />

            {tooltipMode && (
              <PinningTooltip
                message={
                  tooltipMode === "initial"
                    ? "Please click on the image where you would \nlike the edit to be made"
                    : "Please click on the image to add another pin"
                }
              />
            )}

            {imageApproval && editedImage && (
              <BeforeAfterToggle view={beforeAfterView} onChange={handleBeforeAfterChange} />
            )}

            {/* Loading overlay while fetching pins */}
            {isLoadingPins && (
              <div
                className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
                data-testid="artboard-loading-overlay"
              >
                <div className="flex flex-col items-center gap-2">
                  <Loader className="h-8 w-8 animate-spin text-blue-700" />
                  <span className="text-sm font-medium text-gray-700">Loading pins...</span>
                </div>
              </div>
            )}

            {/* Loading overlay while generating edited image */}
            {(isRetryingPreview || (imageApproval && !editedImage)) && (
              <div
                className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
                data-testid="artboard-completing-overlay"
              >
                <div className="flex flex-col items-center gap-2">
                  <Loader className="h-8 w-8 animate-spin text-blue-700" />
                  <span className="text-sm font-medium text-gray-700">
                    Generating edited image...
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Toolbar buttons */}
        <div className="flex shrink-0 items-center justify-center gap-3 py-1">
          <div className="flex h-10 items-center gap-2 rounded-lg bg-white px-4 shadow-md">
            <Switch
              isSelected={showPins}
              onChange={setShowPins}
              data-hj-allow
              data-testid="show-pins-toggle"
            >
              <span className="text-sm font-medium text-gray-700">Show pins</span>
            </Switch>
          </div>
          <RefineZoom maxZoom={10} />
        </div>

        {/* Action bar at bottom */}
        <PinningFeedback />
        <PinningActionBar
          onCancel={handleCancelClick}
          isCancelDisabled={isCancelling || isBulkDeleting || isImageSaving}
          variant="static"
          leftContent={escapeHatchButton}
        />

        <ConfirmActionModal {...cancelModalProps} />
        <ConfirmActionModal
          isOpen={escapeHatchModalOpen}
          onClose={handleEscapeHatchDismiss}
          onConfirm={handleEscapeHatchConfirm}
          title="Use classic edit form?"
          message="This will close the pin editor and let you describe your edits in a text form. Your placed pins will not be saved. Continue?"
          confirmText="Continue"
          cancelText="Stay here"
          isLoading={isCancelling}
          loadingText="Switching..."
        />
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="relative h-full flex-1" data-testid="pinning-artboard">
      <div
        ref={artboardRef}
        className="h-full w-full overflow-hidden rounded-xl"
        data-testid="pinning-artboard-container"
      >
        <ArtboardBase
          readonly={true}
          canvasProps={canvasProps}
          controls={controls}
          backgroundType={backgroundType}
          onBackgroundChange={setBackgroundType}
          currentSide={side}
          zoomConfig={{ initialZoom: 2, maxZoom: 10 }}
          loadingState={{ isLoading: false, imagePreview: undefined }}
          frontLoadingState={{ isLoading: false, imagePreview: undefined }}
          backLoadingState={{ isLoading: false, imagePreview: undefined }}
          mobileSizePlacementOpen={isMobileSizePlacementOpen}
          onMobileSizePlacementChange={setIsMobileSizePlacementOpen}
        />
      </div>

      {tooltipMode && (
        <PinningTooltip
          message={
            tooltipMode === "initial"
              ? "Please click on the image where you would \nlike the edit to be made"
              : "Please click on the image to add another pin"
          }
        />
      )}

      {imageApproval && editedImage && (
        <BeforeAfterToggle view={beforeAfterView} onChange={handleBeforeAfterChange} />
      )}

      {!imageApproval && (
        <div className="absolute right-[140px] top-2 z-10 flex h-9 items-center gap-2 rounded-md bg-white px-3 shadow-md">
          <Switch
            isSelected={showPins}
            onChange={setShowPins}
            data-hj-allow
            data-testid="show-pins-toggle"
          >
            <span className="text-sm font-medium text-gray-700">Show pins</span>
          </Switch>
        </div>
      )}

      {/* Loading overlay while fetching pins */}
      {isLoadingPins && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
          data-testid="artboard-loading-overlay"
        >
          <div className="flex flex-col items-center gap-2">
            <Loader className="h-8 w-8 animate-spin text-blue-700" />
            <span className="text-sm font-medium text-gray-700">Loading pins...</span>
          </div>
        </div>
      )}

      {/* Loading overlay while generating edited image */}
      {(isRetryingPreview || (imageApproval && !editedImage)) && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
          data-testid="artboard-completing-overlay"
        >
          <div className="flex flex-col items-center gap-2">
            <Loader className="h-8 w-8 animate-spin text-blue-700" />
            <span className="text-sm font-medium text-gray-700">Generating edited image...</span>
          </div>
        </div>
      )}

      <PinningFeedback />

      <PinningActionBar
        onCancel={handleCancelClick}
        isCancelDisabled={isCancelling || isBulkDeleting || isImageSaving}
        leftContent={escapeHatchButton}
      />

      <ConfirmActionModal {...cancelModalProps} />
      <ConfirmActionModal
        isOpen={escapeHatchModalOpen}
        onClose={handleEscapeHatchDismiss}
        onConfirm={handleEscapeHatchConfirm}
        title="Use classic edit form?"
        message="This will close the pin editor and let you describe your edits in a text form. Your placed pins will not be saved. Continue?"
        confirmText="Continue"
        cancelText="Stay here"
        isLoading={isCancelling}
        loadingText="Switching..."
      />
    </div>
  );
}
